import { uniqWith } from 'es-toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import { Checkbox, Input, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';

import { PlateList, PlateOption } from '@paragon/contract-library';
import {
  comparePlateGeometryType,
  getPlateManufacturer,
  getPlateSizeString,
  getPlateTypeAndSizeString,
} from '@paragon/material-lists';
import { sortBy } from '@paragon/utilities';

import { comboPlateTypesToTangiblePlateTypes } from '../../util/combo-plate';
import { getPlateManufacturerFormatter } from '../../util/plate-manufacturers';
import { useAppSelector } from '../hooks';

interface Column {
  readonly name: string;
  readonly title?: string;
  readonly getCellValue: (row: PlateOption) => string;
}

interface PlateOptionsGridProps {
  readonly plateOptions: readonly PlateOption[];
  readonly plateList: PlateList;
  readonly setEditedPlateList: (
    updater: (oldPlateListPlates: readonly PlateOption[]) => readonly PlateOption[],
  ) => void;
  readonly showOnlyEnabled: boolean;
}

export function PlateOptionsGrid({
  plateOptions,
  plateList,
  setEditedPlateList,
  showOnlyEnabled,
}: PlateOptionsGridProps) {
  const formatPlateManufacturer = useAppSelector(getPlateManufacturerFormatter);

  const columns: readonly Column[] = useMemo(
    () => [
      {
        name: 'manufacturer',
        title: 'Manufacturer',
        getCellValue: (row: PlateOption) => formatPlateManufacturer(getPlateManufacturer(row.plateType)),
      },
      {
        name: 'plateType',
        title: 'Type',
        getCellValue: (row: PlateOption) => row.plateType,
      },
      {
        name: 'size',
        title: 'Size (width x length)',
        getCellValue: getPlateSizeString,
      },
    ],
    [formatPlateManufacturer],
  );

  const [manufacturerFilter, setManufacturerFilter] = useState('');
  const [plateTypeFilter, setPlateTypeFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');

  const tangiblePlateListPlates = comboPlateTypesToTangiblePlateTypes(plateList.plates);

  const plateListOptionSet = useMemo(
    () => new Set(tangiblePlateListPlates.map((plate) => getPlateTypeAndSizeString(plate, false))),
    [tangiblePlateListPlates],
  );

  const sortedFilteredPlateOptions = useMemo(() => {
    let filteredRows = plateOptions;
    if (showOnlyEnabled) {
      filteredRows = filteredRows.filter((row) => plateListOptionSet.has(getPlateTypeAndSizeString(row, false)));
    }
    if (manufacturerFilter !== '') {
      filteredRows = filteredRows.filter((plateOption) => {
        const manufacturer = formatPlateManufacturer(getPlateManufacturer(plateOption.plateType));
        return manufacturer.toLowerCase().includes(manufacturerFilter);
      });
    }
    if (plateTypeFilter !== '') {
      filteredRows = filteredRows.filter((plateOption) =>
        plateOption.plateType.toLowerCase().includes(plateTypeFilter),
      );
    }
    if (sizeFilter !== '') {
      filteredRows = filteredRows.filter((plateOption) => {
        const plateSize = getPlateSizeString(plateOption, false);
        return plateSize.toLowerCase().includes(sizeFilter);
      });
    }

    return sortBy(filteredRows, [
      (plateOption) => formatPlateManufacturer(getPlateManufacturer(plateOption.plateType)),
      (plateOption) => plateOption.plateType,
      (plateOption) => plateOption.width,
      (plateOption) => plateOption.length,
      (plateOption) => comparePlateGeometryType(plateOption.geometryType),
    ]);
  }, [
    plateOptions,
    showOnlyEnabled,
    manufacturerFilter,
    plateTypeFilter,
    sizeFilter,
    plateListOptionSet,
    formatPlateManufacturer,
  ]);
  const sortedFilteredPlateOptionKeys = useMemo(
    () => sortedFilteredPlateOptions.map((plate) => getPlateTypeAndSizeString(plate, false)),
    [sortedFilteredPlateOptions],
  );

  // A bevel plate row represents both the left- and right-handed versions. Including a row means that both should be
  // included in the list, and excluding a row means that both should be excluded. Left- and right-handed versions will
  // always come together in the prototypes list. The distinction exists for plate geometry purposes but is not relevant
  // to inventory since those building trusses must stock an equal number of left- and right-handed bevel plates. Here
  // we filter to only the left-handed versions so that we only have one row for each bevel plate size. Elsewhere in
  // this file, PlateOptions for both variants will be present.
  const sortedPlateOptionRows = useMemo(
    () => sortedFilteredPlateOptions.filter((plateOption) => plateOption.geometryType !== 'BevelPlateRightHanded'),
    [sortedFilteredPlateOptions],
  );

  const allRowsChecked = useMemo(
    () =>
      sortedFilteredPlateOptions.every((plateOption) =>
        plateListOptionSet.has(getPlateTypeAndSizeString(plateOption, false)),
      ),
    [sortedFilteredPlateOptions, plateListOptionSet],
  );
  const someRowsChecked = plateListOptionSet.size > 0;

  const fixedHeaderContent = useCallback(
    () => (
      <>
        <TableRow>
          <TableCell size="small">
            <Checkbox
              checked={allRowsChecked}
              indeterminate={!allRowsChecked && someRowsChecked}
              onChange={() => {
                if (allRowsChecked) {
                  setEditedPlateList((oldPlateListPlates) =>
                    oldPlateListPlates.filter(
                      (oldPlateListPlateOption) =>
                        !sortedFilteredPlateOptionKeys.includes(
                          // Unchecking all rows should remove all bevel plate options, so we don't need to worry about
                          // the left/right-handed distinction here; comparing suffixes would be extraneous and not work
                          // since `sortedFilteredPlateOptionKeys` is constructed without suffixes
                          getPlateTypeAndSizeString(oldPlateListPlateOption, false),
                        ),
                    ),
                  );
                } else {
                  setEditedPlateList((oldPlateListPlates) =>
                    uniqWith(
                      [...oldPlateListPlates, ...sortedFilteredPlateOptions],
                      // Comparing using suffixes here ensures that both left- and right-handed bevel plate options are
                      // added when checking all rows
                      (a, b) => getPlateTypeAndSizeString(a, true) === getPlateTypeAndSizeString(b, true),
                    ),
                  );
                }
              }}
            />
            {allRowsChecked ? 'Deselect All' : 'Select All'}
          </TableCell>
          <TableCell size="small">Manufacturer</TableCell>
          <TableCell size="small">Type</TableCell>
          <TableCell size="small">Size (width x length)</TableCell>
        </TableRow>
        <TableRow>
          <TableCell size="small" />
          <TableCell size="small">
            <Input
              placeholder="Filter..."
              fullWidth
              onChange={(event) => void setManufacturerFilter(event.target.value.toLowerCase())}
            />
          </TableCell>
          <TableCell size="small">
            <Input
              placeholder="Filter..."
              fullWidth
              onChange={(event) => void setPlateTypeFilter(event.target.value.toLowerCase())}
            />
          </TableCell>
          <TableCell size="small">
            <Input
              placeholder="Filter..."
              fullWidth
              onChange={(event) => void setSizeFilter(event.target.value.toLowerCase())}
            />
          </TableCell>
        </TableRow>
      </>
    ),
    [allRowsChecked, someRowsChecked, setEditedPlateList, sortedFilteredPlateOptionKeys, sortedFilteredPlateOptions],
  );

  const rowContent = useCallback(
    (_: number, plateOption: PlateOption) => (
      <>
        <TableCell size="small">
          <Checkbox
            // Use `getPlateTypeAndSizeString` (without suffix comparison) because that is how `plateListOptionSet` is
            // constructed; bevel plates in the plate list are represented without left/right-handedness
            checked={plateListOptionSet.has(getPlateTypeAndSizeString(plateOption, false))}
            onChange={(_, includeInList) => {
              if (includeInList) {
                if (
                  plateOption.geometryType === 'BevelPlateLeftHanded' ||
                  plateOption.geometryType === 'BevelPlateRightHanded'
                ) {
                  // Selecting a bevel plate row always includes both the left- and right-handed versions
                  setEditedPlateList((oldPlateListPlates) => [
                    ...oldPlateListPlates,
                    { ...plateOption, geometryType: 'BevelPlateLeftHanded' },
                    { ...plateOption, geometryType: 'BevelPlateRightHanded' },
                  ]);
                } else {
                  setEditedPlateList((oldPlateListPlates) => [...oldPlateListPlates, plateOption]);
                }
              } else {
                // Comparing without the bevel plate suffix here means that unchecking a bevel plate row will remove
                // both the left- and right-handed bevel plate options from the plate list, which is what we want
                const plateToRemovePlateOptionId = getPlateTypeAndSizeString(plateOption, false);
                setEditedPlateList((oldPlateListPlates) =>
                  oldPlateListPlates.filter((plate) => getPlateTypeAndSizeString(plate) !== plateToRemovePlateOptionId),
                );
              }
            }}
          />
        </TableCell>
        {columns.map((column) => (
          <TableCell size="small" key={column.name}>
            {column.getCellValue(plateOption)}
          </TableCell>
        ))}
      </>
    ),
    [columns, plateListOptionSet, setEditedPlateList],
  );

  return (
    <TableVirtuoso
      data={sortedPlateOptionRows}
      components={VirtuosoTableComponents}
      fixedHeaderContent={fixedHeaderContent}
      itemContent={rowContent}
    />
  );
}

const VirtuosoTableComponents: TableComponents<PlateOption> = {
  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => <TableContainer {...props} ref={ref} />),
  Table: (props) => <Table {...props} style={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />,
  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (
    <TableHead {...props} ref={ref} sx={{ bgcolor: 'background.paper' }} />
  )),
  TableRow,
  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => <TableBody {...props} ref={ref} />),
};
